// src/components/InsightsPage.js
import React, { useMemo, useState, useEffect } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { Link } from "react-router-dom";
import "../Style/Insights.css";
import { INSIGHTS_LINKS, INSIGHTS_REGIONS } from "../utils/insightsLinks";

const FEATURED = [
  {
    title: "KPMG Africa Centre of Excellence",
    description: "Curated insights from the Centre — connecting innovators, investors and partners across Africa.",
    to: "/about",
    region: "Africa",
    tag: "Platform",
  },
  {
    title: "Venture Pulse — Global trends",
    description: "KPMG Private Enterprise's quarterly look at venture capital activity and global trends.",
    href: "https://kpmg.com/xx/en/our-insights/operations/q2-venture-pulse-report-global.html",
    region: "Global",
    tag: "VC & Deals",
  },
  {
    title: "KPMG CEO Outlook",
    description: "How CEOs are positioning their organisations for resilience and growth in a shifting global economy.",
    href: "https://kpmg.com/xx/en/our-insights/value-creation/kpmg-2024-ceo-outlook-the-private-company-perspective.html",
    region: "Global",
    tag: "Leadership",
  },
  {
    title: "Private Enterprise — Africa",
    description: "KPMG's latest thought leadership specifically for Africa's private enterprise and entrepreneurship ecosystem.",
    href: "https://kpmg.com/ke/en/services/enterprise.html",
    region: "Africa",
    tag: "Private Enterprise",
  },
  {
    title: "Africa Private Equity & VC",
    description: "Trends shaping private equity and venture capital markets across the African continent.",
    href: "https://kpmg.com/za/en/industries/private-equity.html",
    region: "Africa",
    tag: "Investment",
  },
  {
    title: "Family Business Report",
    description: "KPMG's global family business survey — resilience, succession and growth strategies for multi-generational businesses.",
    href: "https://kpmg.com/xx/en/our-insights/value-creation/family-business-survey.html",
    region: "Global",
    tag: "Family Business",
  },
];

const REGION_ACCENT = {
  Global: "#00B8F5",
  Africa: "#FD349C",
  Europe: "#7213EA",
  "Middle East": "#00C0AE",
  "Asia-Pacific": "#00338D",
  Americas: "#76D2FF",
};

// KPMG RSS endpoints to try via rss2json proxy
const RSS_FEEDS = [
  {
    label: "KPMG Global Insights",
    url: "https://kpmg.com/xx/en/our-insights.rss",
  },
  {
    label: "KPMG Private Enterprise",
    url: "https://kpmg.com/xx/en/home/insights.rss",
  },
];

const RSS2JSON = "https://api.rss2json.com/v1/api.json?rss_url=";

function useLiveFeed() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchFeeds() {
      setLoading(true);
      setError(false);
      const results = [];

      for (const feed of RSS_FEEDS) {
        try {
          const res = await fetch(
            `${RSS2JSON}${encodeURIComponent(feed.url)}&count=4`,
            { signal: AbortSignal.timeout(6000) }
          );
          if (!res.ok) continue;
          const data = await res.json();
          if (data.status === "ok" && Array.isArray(data.items)) {
            data.items.slice(0, 4).forEach((item) => {
              results.push({
                title: item.title,
                description: item.description
                  ? item.description.replace(/<[^>]*>/g, "").slice(0, 160) + "…"
                  : "",
                link: item.link,
                pubDate: item.pubDate ? item.pubDate.slice(0, 10) : "",
                source: feed.label,
              });
            });
          }
        } catch {
          // silently skip feed if unreachable
        }
      }

      if (!cancelled) {
        if (results.length > 0) {
          setArticles(results.slice(0, 8));
        } else {
          setError(true);
        }
        setLoading(false);
      }
    }

    fetchFeeds();
    return () => { cancelled = true; };
  }, []);

  return { articles, loading, error };
}

export default function InsightsPage() {
  const { t } = useLanguage();
  const [search, setSearch] = useState("");
  const [activeRegion, setActiveRegion] = useState("All");
  const { articles, loading: feedLoading, error: feedError } = useLiveFeed();

  const matchesTerm = (item, term) => {
    if (!term) return true;
    if (item.name.toLowerCase().includes(term)) return true;
    if (item.region && item.region.toLowerCase().includes(term)) return true;
    if (Array.isArray(item.aliases))
      return item.aliases.some((a) => a.toLowerCase().includes(term));
    return false;
  };

  const grouped = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matches = INSIGHTS_LINKS.filter((item) => {
      if (!matchesTerm(item, term)) return false;
      if (activeRegion !== "All" && item.region !== activeRegion) return false;
      return true;
    });
    const groups = {};
    for (const item of matches) {
      if (!groups[item.region]) groups[item.region] = [];
      groups[item.region].push(item);
    }
    Object.values(groups).forEach((arr) =>
      arr.sort((a, b) => a.name.localeCompare(b.name))
    );
    return groups;
  }, [search, activeRegion]);

  const totalCount = INSIGHTS_LINKS.filter((i) =>
    matchesTerm(i, search.trim().toLowerCase())
  ).length;

  const regionOrder = ["Global", ...INSIGHTS_REGIONS];

  return (
    <div className="insights-page">

      {/* ── HERO ─────────────────────────────────────────── */}
      <header className="insights-hero">
        <div className="insights-hero-glow glow-blue" aria-hidden="true" />
        <div className="insights-hero-glow glow-pink" aria-hidden="true" />
        <div className="insights-hero-glow glow-teal" aria-hidden="true" />
        <div className="insights-hero-grid" aria-hidden="true" />

        <div className="insights-hero-inner">
          <span className="insights-eyebrow">{t('insights_title')}</span>
          <h1>{t('insights_subtitle')}</h1>
          <p className="insights-lede">
            Sector intelligence, market analyses and thought leadership from
            KPMG's global network. Choose a region or country to explore curated
            knowledge for your market.
          </p>

          {/* Hero search */}
          <div className="insights-hero-search">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
              <path d="M20 20l-3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              placeholder="Search by country or region…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search insights"
            />
            {search && (
              <button type="button" onClick={() => setSearch("")} aria-label="Clear">×</button>
            )}
          </div>

        </div>
      </header>

      {/* ── LIVE FEED ─────────────────────────────────────── */}
      <section className="insights-live-section">
        <div className="insights-live-header">
          <h2>Latest from KPMG</h2>
        </div>

        {feedLoading && (
          <div className="insights-live-grid">
            {[1,2,3,4].map((i) => (
              <div key={i} className="insights-live-skeleton">
                <div className="skeleton-line short" />
                <div className="skeleton-line" />
                <div className="skeleton-line medium" />
              </div>
            ))}
          </div>
        )}

        {!feedLoading && feedError && (
          <div className="insights-live-fallback">
            <p>
              Browse curated insights from KPMG below, or visit{" "}
              <a href="https://kpmg.com/xx/en/our-insights.html" target="_blank" rel="noopener noreferrer">
                kpmg.com/insights
              </a>{" "}
              for the full library of global thought leadership.
            </p>
          </div>
        )}

        {!feedLoading && !feedError && articles.length > 0 && (
          <div className="insights-live-grid">
            {articles.map((a, i) => (
              <a
                key={i}
                href={a.link}
                target="_blank"
                rel="noopener noreferrer"
                className="insights-live-card"
              >
                <span className="insights-live-source">{a.source}</span>
                <h3>{a.title}</h3>
                <p>{a.description}</p>
                <div className="insights-live-footer">
                  {a.pubDate && <span className="insights-live-date">{a.pubDate}</span>}
                  <span className="insights-live-cta">Read →</span>
                </div>
              </a>
            ))}
          </div>
        )}
      </section>

      {/* ── FEATURED ─────────────────────────────────────── */}
      <section className="insights-featured">
        <div className="insights-section-heading">
          <h2>Featured Insights</h2>
          <span className="insights-section-sub">Curated for Africa's private enterprise ecosystem</span>
        </div>
        <div className="insights-featured-grid">
          {FEATURED.map((f) => {
            const cardInner = (
              <>
                <div className="insights-featured-card-top">
                  <span className="insights-featured-region">{f.region}</span>
                  <span className="insights-featured-tag">{f.tag}</span>
                </div>
                <h3>{f.title}</h3>
                <p>{f.description}</p>
                <span className="insights-featured-link">Read insights →</span>
              </>
            );
            return f.to ? (
              <Link
                key={f.title}
                to={f.to}
                className="insights-featured-card"
                style={{ "--accent": REGION_ACCENT[f.region] || "#00338D" }}
              >
                {cardInner}
              </Link>
            ) : (
              <a
                key={f.title}
                className="insights-featured-card"
                href={f.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{ "--accent": REGION_ACCENT[f.region] || "#00338D" }}
              >
                {cardInner}
              </a>
            );
          })}
        </div>
      </section>

      {/* ── REGION TABS ──────────────────────────────────── */}
      <section className="insights-toolbar" aria-label="Filter insights">
        <div className="insights-region-tabs" role="tablist">
          {["All", ...regionOrder].map((r) => (
            <button
              key={r}
              role="tab"
              type="button"
              className={`insights-region-tab ${activeRegion === r ? "active" : ""}`}
              style={activeRegion === r ? {} : { "--tab-accent": REGION_ACCENT[r] || "#00338D" }}
              onClick={() => setActiveRegion(r)}
            >
              {r}
            </button>
          ))}
        </div>
      </section>

      {/* ── RESULTS ─────────────────────────────────────── */}
      {totalCount === 0 && (
        <p className="insights-empty">
          No matches for <strong>"{search}"</strong>. Try a different term or clear filters.
        </p>
      )}

      <div className="insights-regions-wrapper">
        {regionOrder.map((region) => {
          const list = grouped[region];
          if (!list || list.length === 0) return null;
          return (
            <section key={region} className="insights-region-section">
              <header className="insights-region-header">
                <span
                  className="insights-region-marker"
                  style={{ background: REGION_ACCENT[region] || "#00338D" }}
                  aria-hidden="true"
                />
                <h2 className="insights-section-title">{region}</h2>
                <span className="insights-region-count">{list.length}</span>
              </header>
              <ul className="insights-country-grid">
                {list.map((item) => (
                  <li key={item.name}>
                    <a
                      className="insights-country-link"
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ "--region-accent": REGION_ACCENT[region] || "#00338D" }}
                    >
                      <span className="insights-country-name">{item.name}</span>
                      <span className="insights-country-arrow" aria-hidden="true">↗</span>
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>

      {/* ── FOOTER CTA ───────────────────────────────────── */}
      <footer className="insights-footer-cta">
        <div className="insights-footer-cta-inner">
          <div>
            <h3>Looking for specific advisory?</h3>
            <p>Speak with a KPMG specialist about your market and investment goals.</p>
          </div>
          <Link to="/contact" className="insights-cta-link">Contact us →</Link>
        </div>
      </footer>
    </div>
  );
}
